import React, { useRef, useState } from 'react';
import { Col, Row } from 'reactstrap';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import moment from 'moment';
import SlickCarousel from '../../common/components/SlickCarousel';
import AuctionImageCarousel from '../../common/components/new/AuctionImageCarousel';
import { ViewType } from '../../common/components/new/AuctionListTemplate';
import CommonButton from '../../common/components/CommonButton';
import { CommonButtonVariants } from '../../constants/CommonButtonVariants';
import useCountdownTimer from '../../common/hooks/useCountdownTimer';
import { capitalizeString } from '../../common/helpers/capitalizeString';
import RouterPaths from '../../constants/RouterPaths';
import locationIcon from '../../assets/img/icons/location_on.svg';
import calendarMonthIcon from '../../assets/img/icons/calendar-month.svg';
import milesIcon from '../../assets/img/icons/miles.svg';
import autoTransmissionIcon from '../../assets/img/icons/auto_transmission.svg';
import fuelIcon from '../../assets/img/icons/fuel.svg';
import checkWhiteIcon from '../../assets/img/icons/check-white.svg';
import leftChevronIcon from '../../assets/img/icons/left-chevron.svg';
import rightChevronIcon from '../../assets/img/icons/right-chevron.svg';

const NOT_DEFINED = 'Not defined';

function CarouselItem({ item }) {
  const navigate = useNavigate();
  const photos = item?.media?.photos || [];
  const videos = item?.media?.videos || [];

  const makeName = item?.vehicleModel?.makeName || '';
  const name = item?.vehicleModel?.name || '';
  const title = [makeName, name].filter(Boolean).join(' ') || NOT_DEFINED;
  const derivative = item?.derivative || '';
  const vehicleHighlightedInfo = item?.vehicleHighlightedInfo || '';

  const location = [item?.city, item?.county].filter(Boolean).join(', ');

  const secondsLeft = item?.endsOn
    ? Math.max(moment(item.endsOn).diff(moment(), 'seconds'), 0)
    : 0;
  const timeLeft = useCountdownTimer(secondsLeft);

  const specifications = [
    { icon: calendarMonthIcon, value: item?.year || NOT_DEFINED },
    { icon: milesIcon, value: item?.mileage ? `${item.mileage} miles` : NOT_DEFINED },
    {
      icon: autoTransmissionIcon,
      value: item?.transmission ? capitalizeString(item.transmission) : NOT_DEFINED,
    },
    { icon: fuelIcon, value: item?.fuel ? capitalizeString(item.fuel) : NOT_DEFINED },
  ];

  const dragStartRef = useRef({ x: 0, y: 0 });

  const handlePointerDown = (e) => {
    dragStartRef.current = { x: e.clientX, y: e.clientY };
  };

  const goToLogin = () => navigate(RouterPaths.LOGIN);

  const handleCardClick = (e) => {
    const dx = Math.abs(e.clientX - dragStartRef.current.x);
    const dy = Math.abs(e.clientY - dragStartRef.current.y);
    if (dx > 5 || dy > 5) return;
    goToLogin();
  };

  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      goToLogin();
    }
  };

  const handleRegisterClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    navigate(RouterPaths.REGISTER);
  };

  return (
    <div
      className="recently-launched-carousel__item"
      role="link"
      tabIndex={0}
      style={{ cursor: 'pointer' }}
      onMouseDown={handlePointerDown}
      onClick={handleCardClick}
      onKeyDown={handleCardKeyDown}
    >
      <div className="auction-list-item auction-list-item--grid m-0 p-0">
        <div className="auction-list-item__media-box position-relative d-flex justify-content-center align-items-center">
          <div style={{ display: 'block', width: '100%', height: 220 }}>
            <AuctionImageCarousel
              photos={photos}
              videos={videos}
              height={220}
              width="100%"
              viewType={ViewType.GRID}
            />
          </div>
        </div>
        <div className="auction-list-item__content-box auction-list-item__content-box__grid">
          <div className="d-flex justify-content-between mb-2 text-start">
            <Row className="auction-list-item__car-info">
              <span>{title}</span>
              <span>{derivative}</span>
            </Row>
          </div>

          <Row className="auction-list-item-row">
            <Col sm={12} className="auction-list-item__info-box">
              <div className="d-flex flex-column gap-1 recently-launched-carousel__badges">
                {Boolean(vehicleHighlightedInfo) && (
                  <div className="auction-list-item__badge auction-list-item__badge--special align-self-start">
                    <span>{vehicleHighlightedInfo}</span>
                  </div>
                )}
                {Boolean(location) && (
                  <div className="auction-list-item__badge align-self-start py-1">
                    <img src={locationIcon} alt="Vehicle Location" />
                    <span>{location}</span>
                  </div>
                )}
              </div>
              <div className="auction-list-item__specification-list">
                {specifications.map((spec) => (
                  <div
                    key={`${spec.icon}-${spec.value}`}
                    className="auction-list-item__specification-list-item auction-list-item__specification-list-item__grid"
                  >
                    <div>
                      <img src={spec.icon} alt="i" />
                    </div>
                    <span>{spec.value}</span>
                  </div>
                ))}
              </div>
            </Col>
            <Col sm={12} className="mt-3">
              <div className="auction-list-item__bid-info">
                <div className="auction-list-item__time-info">
                  <span>{timeLeft}</span>
                  <span>Time left</span>
                </div>
                <div className="w-100 mt-2 px-2 pt-0 register-btn-wrapper">
                  <CommonButton
                    type="button"
                    label="REGISTER TO BID"
                    className="auction-place-bid-box__send-button text-sm w-100"
                    variant={CommonButtonVariants.RED}
                    icon={checkWhiteIcon}
                    iconAlt="Register to bid"
                    iconLeft
                    handleClick={handleRegisterClick}
                  />
                </div>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </div>
  );
}

CarouselItem.propTypes = {
  item: PropTypes.object.isRequired,
};

const carouselSettings = {
  adaptiveHeight: true,
  variableWidth: true,
  swipe: true,
  appendArrows: '.recently-launched-carousel__buttons',
  arrows: false,
  autoplay: true,
  autoplaySpeed: 8000,
  pauseOnHover: true,
};

function RecentlyLaunchedSection({ items }) {
  const slickRef = useRef(null);
  const carouselItems = items || [];
  const [autoplayEnabled, setAutoplayEnabled] = useState(true);

  const stopAutoplay = () => {
    if (slickRef?.current) {
      slickRef.current.slickPause();
    }
  };

  const disableAutoplay = () => {
    setAutoplayEnabled(false);
    stopAutoplay();
  };

  const carouselDragRef = useRef(null);

  const handleCarouselPointerDown = (e) => {
    const point = e.touches?.[0] ?? e;
    carouselDragRef.current = { x: point.clientX, y: point.clientY };
  };

  const handleCarouselPointerUp = (e) => {
    if (!carouselDragRef.current) return;
    const point = e.changedTouches?.[0] ?? e;
    const dx = Math.abs(point.clientX - carouselDragRef.current.x);
    const dy = Math.abs(point.clientY - carouselDragRef.current.y);
    carouselDragRef.current = null;
    if (dx > 10 || dy > 10) {
      disableAutoplay();
    }
  };

  const settings = {
    ...carouselSettings,
    infinite: true,
    autoplay: autoplayEnabled,
    onSwipe: disableAutoplay,
  };

  const nextItem = () => {
    if (slickRef?.current) {
      slickRef.current.slickNext();
      stopAutoplay();
    }
  };

  const prevItem = () => {
    if (slickRef?.current) {
      slickRef.current.slickPrev();
      stopAutoplay();
    }
  };

  return (
    <section className="recently-launched section">
      <Row className="w-100 mb-5 recently-launched-auctions__top-section">
        <Col className="text-right mb-3">
          <span className="sell-stock-section__heading">Latest Vehicles Added</span>
        </Col>
        <Col>
          <div className="recently-launched-carousel__buttons">
            <button onClick={prevItem} className="carousel-button-left mr-2" type="button">
              <img src={leftChevronIcon} alt="recently-launched-left-icon" />
            </button>
            <button onClick={nextItem} className="carousel-button-right ml-2" type="button">
              <img src={rightChevronIcon} alt="recently-launched-right-icon" />
            </button>
          </div>
        </Col>
      </Row>
      <Row className="w-100">
        <Col
          md={12}
          className="w-100 recently-launched-carousel"
          onMouseDown={handleCarouselPointerDown}
          onMouseUp={handleCarouselPointerUp}
          onTouchStart={handleCarouselPointerDown}
          onTouchEnd={handleCarouselPointerUp}
        >
          <SlickCarousel
            settings={settings}
            slickRef={slickRef}
            items={carouselItems.map((el) => (
              <CarouselItem key={el.auctionId} item={el} />
            ))}
          />
        </Col>
      </Row>
    </section>
  );
}

RecentlyLaunchedSection.propTypes = {
  items: PropTypes.array.isRequired,
};

export default RecentlyLaunchedSection;
